/**
 * Import d'un export **Sticky Password** XML (« Menu > Exporter > Exporter les
 * comptes Web et d'applications > XML » de Sticky Password : ses utilisateurs
 * doivent pouvoir partir avec TOUT leur coffre).
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le document `<root><Database>` porte trois listes :
 * `Groups > Group` (`ID`, `ParentID`, `Name` : l'arborescence des dossiers),
 * `Logins > Login` (`ID`, `Name` = identifiant, `Password` ; `RealLogin` =
 * identifiant reel quand le compte a plusieurs utilisateurs, `Name` n'etant
 * alors qu'un libelle) et
 * `Accounts > Account` (`Name`, `Link`, `Comments`, `ParentID` = son groupe),
 * chaque compte designant ses identifiants par `LoginLinks > Login
 * SourceLoginID`. Un compte a plusieurs identifiants donne une entree par
 * identifiant ; un compte sans identifiant et un identifiant sans compte sont
 * importes quand meme (aucune perte). Tout attribut non repris devient un
 * champ personnalise, masque si son libelle designe un secret.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, type ImportedEntry } from './csv-import.js';
import { childNamed, childrenNamed, parseXml, type XmlNode } from './kdbx-import.js';

/** Attributs repris ailleurs, ou purement techniques (identifiants internes, horodatages). */
const KNOWN_ATTRIBUTE = /^(?:ID|ParentID|SourceLoginID|Name|RealLogin|Password|Link|Comments)$|(?:ID|Date|Time)$/u;

/** Adresse web (schema `x://` ou nom d'hote nu), par opposition au chemin d'une application. */
const WEB_LINK = /^[a-z][a-z0-9+.-]*:\/\/|^(?:[\w-]+\.)+[a-z]{2,}(?:[:/?#]|$)/iu;

/** Le document Sticky Password, ou `null` si le texte n'en est pas un. */
function stickyDatabase(text: string): XmlNode | null {
  if (!/^[\s\uFEFF]*(?:<\?xml[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*<root[\s>]/u.test(text)) return null;
  let document: XmlNode;
  try {
    document = parseXml(text);
  } catch {
    return null;
  }
  const database = childNamed(childNamed(document, 'root'), 'Database');
  if (database === null) return null;
  return childNamed(database, 'Logins') !== null || childNamed(database, 'Accounts') !== null ? database : null;
}

/**
 * Sticky Password ecrit les sauts de ligne des commentaires en `/n` litteral :
 * ils redeviennent de vrais sauts de ligne. Une adresse (`https://x.fr/news`)
 * n'est pas touchee : son `/n` fait partie du chemin.
 */
function stickyNotes(raw: string): string {
  return raw.replace(/\S*:\/\/\S*|\/n/gu, (match) => (match === '/n' ? '\n' : match));
}

/** Nom de repli d'une entree : l'hote de son adresse, sans `www.`. */
function hostOf(url: string): string {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//iu.test(url) ? url : `https://${url}`).hostname.replace(/^www\./iu, '');
  } catch {
    return url;
  }
}

/** Attributs non repris d'un element : un champ chacun, masque si secret. */
function extraFields(node: XmlNode): EntryCustomField[] {
  return Object.entries(node.attributes)
    .filter(([key, value]) => !KNOWN_ATTRIBUTE.test(key) && value.trim().length > 0)
    .map(([key, value]) => csvColumnField(key, value.trim()));
}

/** Chemin du dossier d'un groupe, de la racine vers la feuille (garde contre un cycle de `ParentID`). */
function folderPathOf(groupId: string, groups: ReadonlyMap<string, XmlNode>): string[] {
  const path: string[] = [];
  const seen = new Set<string>();
  let current = groups.get(groupId);
  while (current !== undefined) {
    const id = current.attributes['ID'] ?? '';
    if (seen.has(id)) break;
    seen.add(id);
    const name = (current.attributes['Name'] ?? '').trim();
    if (name.length > 0) path.unshift(name);
    current = groups.get(current.attributes['ParentID'] ?? '');
  }
  return path;
}

function withFields(customFields: readonly EntryCustomField[]): { customFields?: readonly EntryCustomField[] } {
  return customFields.length > 0 ? { customFields } : {};
}

/** Horodatage Sticky Password (.NET) : `2021-01-02T07:28:35.771+05:00`, fuseau facultatif. */
const STICKY_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/u;

/**
 * Date du mot de passe : Sticky Password date l'identifiant (`Login`, qui ne
 * porte que nom et mot de passe) ; `ModifiedDate` (derniere modification) =
 * borne haute, jamais `CreatedDate` ni les dates du compte. Sans fuseau, lue
 * en UTC (juste a quelques heures pres). Aucune date sans mot de passe ; date
 * minimale .NET (an 1), date impossible (31 fevrier), absente ou illisible :
 * l'import vaut pose.
 */
function stickyPasswordChangedAt(login: XmlNode, password: string): { passwordChangedAt?: number } {
  const raw = (login.attributes['ModifiedDate'] ?? '').trim();
  const match = password.length > 0 ? STICKY_DATETIME.exec(raw) : null;
  if (match === null) return {};
  const [year = 0, month = 0, day = 0] = match.slice(1, 4).map(Number);
  // Un 31 fevrier, que `Date.parse` reporterait au 3 mars, est illisible : jamais une date inventee.
  const civil = new Date(Date.UTC(year, month - 1, day));
  if (civil.getUTCDate() !== day || civil.getUTCMonth() !== month - 1) return {};
  const at = Date.parse(match[4] === undefined ? `${raw}Z` : raw);
  return at > 0 ? { passwordChangedAt: at } : {};
}

/** Une entree : un compte (peut manquer) et l'un de ses identifiants (peut manquer). */
function stickyEntry(account: XmlNode | null, login: XmlNode | null, groups: ReadonlyMap<string, XmlNode>): ImportedEntry {
  const link = (account?.attributes['Link'] ?? '').trim();
  const isWeb = WEB_LINK.test(link);
  // Compte a plusieurs utilisateurs : `RealLogin` est l'identifiant, `Name` un simple libelle (garde en champ).
  const loginName = (login?.attributes['Name'] ?? '').trim();
  const realLogin = (login?.attributes['RealLogin'] ?? '').trim();
  const username = realLogin || loginName;
  // Jamais rogne : une espace peut faire partie du mot de passe.
  const password = login?.attributes['Password'] ?? '';
  const customFields = [
    ...(link.length > 0 && !isWeb ? [{ name: 'Application', value: link, hidden: false }] : []),
    ...(realLogin.length > 0 && loginName.length > 0 && loginName !== realLogin
      ? [{ name: 'Libellé', value: loginName, hidden: false }]
      : []),
    ...(account !== null ? extraFields(account) : []),
    ...(login !== null ? extraFields(login) : []),
  ];
  const folderPath = account !== null ? folderPathOf(account.attributes['ParentID'] ?? '', groups) : [];
  const base = {
    username: '',
    password: '',
    uris: [] as string[],
    notes: joinNotes([stickyNotes(account?.attributes['Comments'] ?? '')]),
    totpSecret: '',
    favorite: false,
    ...(folderPath.length > 0 ? { folderPath } : {}),
  };
  const name = (account?.attributes['Name'] ?? '').trim();
  // Ni identifiant, ni mot de passe, ni adresse : c'est une note securisee.
  if (login === null && link.length === 0) {
    return { ...base, type: 'note', name: name || 'Sans nom', ...withFields(customFields) };
  }
  return {
    ...base,
    type: 'login',
    name: name || (isWeb ? hostOf(link) : '') || loginName || username || 'Sans nom',
    username,
    password,
    ...(login !== null ? stickyPasswordChangedAt(login, password) : {}),
    uris: isWeb ? [link] : [],
    ...withFields(customFields),
  };
}

/** Un export Sticky Password se reconnait a son document `<root><Database>` portant `Logins` ou `Accounts`. */
export function isStickyPasswordXml(text: string): boolean {
  return stickyDatabase(text) !== null;
}

/** Parse un export Sticky Password XML : chaque compte avec ses identifiants, rattache a son dossier. */
export function parseStickyPasswordXml(text: string): ImportedEntry[] {
  const database = stickyDatabase(text);
  if (database === null) throw new Error('export Sticky Password : structure inattendue');
  const groups = new Map<string, XmlNode>();
  for (const group of childrenNamed(childNamed(database, 'Groups'), 'Group')) {
    const id = group.attributes['ID'] ?? '';
    if (id.length > 0) groups.set(id, group);
  }
  const logins = new Map<string, XmlNode>();
  for (const login of childrenNamed(childNamed(database, 'Logins'), 'Login')) {
    const id = login.attributes['ID'] ?? '';
    if (id.length > 0) logins.set(id, login);
  }

  const entries: ImportedEntry[] = [];
  const linked = new Set<string>();
  for (const account of childrenNamed(childNamed(database, 'Accounts'), 'Account')) {
    const accountLogins = childrenNamed(childNamed(account, 'LoginLinks'), 'Login')
      .map((link) => link.attributes['SourceLoginID'] ?? '')
      .filter((id) => logins.has(id));
    if (accountLogins.length === 0) {
      entries.push(stickyEntry(account, null, groups));
      continue;
    }
    for (const id of accountLogins) {
      linked.add(id);
      entries.push(stickyEntry(account, logins.get(id)!, groups));
    }
  }
  // Un identifiant qu'aucun compte ne designe est importe seul, jamais perdu.
  for (const [id, login] of logins) {
    if (!linked.has(id)) entries.push(stickyEntry(null, login, groups));
  }
  return entries;
}
